import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { clearEquipmentDetails, createEquipment, getEquipmentDetails, listDeviceSources, listEquipment, updateEquipment } from "../actions/equipmentActions";
import EquipmentModal from "../components/equipment/EquipmentModal";

function EquipmentPage() {

    const dispatch = useDispatch();

    const [open, setOpen] = useState(false);
    const [modalMode, setModalMode] = useState("create");
    const [search, setSearch] = useState("");

    const deviceSourceList = useSelector(
        state => state.deviceSourceList
    );

    const { devices: sourceDevices } = deviceSourceList;

    const equipmentList = useSelector(
        state => state.equipmentList
    );

    const {
        equipment,
        loading
    } = equipmentList;

    // Filter out equipment with null Ids
    const filteredEquipments = equipment?.filter(
        (eq) => eq.DeviceCode
    )

    const equipmentDetails = useSelector(
        state => state.equipmentDetails
    )

    const {
        equipment: selectedEquipment
    } = equipmentDetails;

    useEffect(() => {
        dispatch(listEquipment());
        dispatch(listDeviceSources());
    }, [dispatch]);

    const handleRowClick = async (deviceCode) => {
        setModalMode("view");

        await dispatch(getEquipmentDetails(deviceCode));

        setOpen(true);
    }

    const handleCreate = async (formData) => {
        await dispatch(createEquipment(formData))

        await dispatch(listEquipment())

        setOpen(false);
    };

    const handleUpdate = async (formData) => {
        await dispatch(updateEquipment(formData));

        await dispatch(listEquipment());

        dispatch(clearEquipmentDetails());
        setOpen(false);
    }

    const uniqueEquipment = Array.from(
        new Map(
            (filteredEquipments || []).map(
                item => [item.DeviceCode, item]
            )
        ).values()
    )

    const searchedEquipment = uniqueEquipment.filter((item) => 
        item.DeviceCode?.toLowerCase().includes(search.toLowerCase()) || 
        item.DeviceName?.toLowerCase().includes(search.toLowerCase())
    )

    return (<>
        <div className="w-full px-4 py-8 md:p-10">
            <div className="mb-8 flex flex-col md:flex-row md:justify-between md:items-end gap-4">
                
                <div>
                    <p className="text-sm uppercase tracking-[4px] text-electricBlue font-semibold">
                        Asset Registry
                    </p>

                    <h1 className="mt-2 text-3xl md:text-4xl font-bold text-textPrimary">
                        Equipment Management
                    </h1>

                    <p className="mt-2 text-sm md:text-base text-textSecondary">
                        Manage generators, power panels and monitoring equipment.
                    </p>
                </div>

                <button
                    onClick={() => {
                        dispatch(clearEquipmentDetails());
                        setModalMode("create");
                        setOpen(true)
                    }}
                    className="w-full md:w-auto px-5 py-3 rounded-xl bg-deepNavy text-white hover:bg-navy transition"
                >
                    Register Equipment
                </button>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-6 mb-8">
                <StatCard
                    label="Total Equipment"
                    value={uniqueEquipment.length}
                />

                <StatCard
                    label="Registered Generators"
                    value={
                        uniqueEquipment.filter(
                            x => x.DeviceType.toLowerCase() === "generator"
                        ).length
                    }
                />

                <StatCard
                    label="Registered Power Panels"
                    value={
                        uniqueEquipment.filter(
                            x => x.DeviceType.toLowerCase() === "powerpanel" || x.DeviceType.toLowerCase() === "power_panel"
                        ).length
                    }
                />

                <StatCard
                    label="Active"
                    value={
                        uniqueEquipment.filter(
                            x => x.Status === "A"
                        ).length
                    }
                />
            </div>

            {/* Search */}
            <div className="mb-6">
                <input 
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search device code or device name..."
                    className="w-full px-4 py-3 rounded-xl border border-border bg-white focus:border-electricBlue outline-none"
                />
            </div> 

            <div className="bg-white rounded-3xl overflow-hidden border border-border overflow-x-auto max-w-full">
                <table className="w-full min-w-[900px]">
                    <thead className="bg-surfaceSoft">
                        <tr>
                            <th className="px-6 py-4 text-center text-xs uppercase tracking-[2px] text-textSecondary">Device ID</th>
                            <th className="px-6 py-4 text-center text-xs uppercase tracking-[2px] text-textSecondary">Device Code</th>
                            <th className="px-6 py-4 text-left text-xs uppercase tracking-[2px] text-textSecondary">Device Name</th>
                            <th className="px-6 py-4 text-center text-xs uppercase tracking-[2px] text-textSecondary">Device Type</th>
                            <th className="px-6 py-4 text-center text-xs uppercase tracking-[2px] text-textSecondary">Serial Number</th>
                            <th className="px-6 py-4 text-center text-xs uppercase tracking-[2px] text-textSecondary">Status</th>
                        </tr>
                    </thead>

                    <tbody>
                        {loading ? (
                            <tr className="border-t border-border hover:bg-slate-50 transition">
                                <td
                                    colSpan="6"
                                    className="p-8 text-center text-gray-500"
                                >
                                    Loading equipment...
                                </td>
                            </tr>
                        ) : searchedEquipment.length === 0 ? (
                            <tr className="border-t border-border hover:bg-slate-50 transition">
                                <td colSpan="6" className="p-8 text-center text-gray-500">
                                    No users found
                                </td>
                            </tr>
                        ) : (
                            searchedEquipment.map((item) => (
                                <tr
                                    key={item.DeviceCode}
                                    onClick={() => handleRowClick(item.DeviceCode)}
                                    className="border-t border-border hover:bg-slate-50 transition cursor-pointer"
                                >
                                    {/* Extract the numeric part after _ or - in the device code using a regex */}
                                    <td className="p-4 text-center">{item.DeviceCode.match(/[_-](\d+)$/)[1]}</td>
                                    <td className="p-4 text-center">{item.DeviceCode}</td>
                                    <td className="p-4">{item.DeviceName || ""}</td>
                                    <td className="p-4 text-center">
                                        <span className={`
                                            px-3 py-1 rounded-full text-xs font-semibold 
                                            ${item.DeviceType === "Generator" ? "bg-electricBlue/10 text-electricBlue" : "bg-slate-100 text-slate-600"}  
                                        `}>
                                            {item.DeviceType === "" ? "Unknown" : item.DeviceType}
                                        </span>
                                    </td>
                                    <td className="p-4 text-center">{item.SerialNumber || "-"}</td>
                                    <td className="p-4 text-center">
                                        <span
                                            className={`
                                                px-3 py-1 rounded-full text-xs font-semibold
                                                ${item.Status === "A" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}  
                                            `}
                                        >
                                            {item.Status === "A" ? "Active" : item.Status === "I" ? "Inactive" : "Unknown"}
                                        </span>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>

        {open && (
            <EquipmentModal 
                open={open}
                mode={modalMode}
                equipment={selectedEquipment}
                equipmentList={equipment}
                sourceDevices={sourceDevices}
                onClose={() => {
                    dispatch(clearEquipmentDetails());
                    setOpen(false);
                }}
                onSubmit={handleCreate}
                onUpdate={handleUpdate}
            />
        )}
    </>)
}

function StatCard({ label, value }) {
    return (
        <div className="bg-white border border-border rounded-3xl p-4 md:p-6">
            <p className="text-sm text-textSecondary">
                {label}
            </p>

            <h2 className="mt-2 text-3xl font-bold text-textPrimary">
                {value}
            </h2>
        </div>
    )
}

export default EquipmentPage;

